export default class BundleBuilderComponent extends HTMLElement {
  constructor() {
    super();
    this.selectedProductIds = new Set();
    this.selectedProductQuantities = new Map();
    this.selectedGiftSelections = new Map();
    this.productIndex = new Map();
    this.promotions = [];
    this.isAddingToCart = false;
    this.pendingGiftSelection = {
      promotionId: '',
      optionKeys: []
    };
    this.onComponentClick = this.onComponentClick.bind(this);
    this.onDocumentClickLookFor = this.onDocumentClickLookFor.bind(this);
    this.onDocumentKeydownLookFor = this.onDocumentKeydownLookFor.bind(this);
  }

  connectedCallback() {
    try {
      this.buildProductIndex();
      this.buildPromotions();
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.addEventListener('click', this.onComponentClick);
      this.syncSelectedProductIds();
      this.initCollectionLookFor();
      this.initMobileDrawer();
    } catch (error) {
      console.error('BundleBuilderComponent init error:', error);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onComponentClick);
    this.teardownCollectionLookFor();
    this.teardownGiftOptionsSlider();
  }

  initCollectionLookFor() {
    if (!this.querySelector('[data-bundle-look-for]')) {
      return;
    }
    document.addEventListener('click', this.onDocumentClickLookFor);
    document.addEventListener('keydown', this.onDocumentKeydownLookFor);
  }

  teardownCollectionLookFor() {
    document.removeEventListener('click', this.onDocumentClickLookFor);
    document.removeEventListener('keydown', this.onDocumentKeydownLookFor);
  }

  initMobileDrawer() {
    // Nothing to measure upfront — the aside is always at bottom:0 with no
    // transform, and only the items panel animates via max-height.
  }

  _getMobileItemsPanel() {
    return this.querySelector('[data-bundle-items-panel]');
  }

  _expandItemsPanel(itemsPanel) {
    itemsPanel.style.maxHeight = '50vh';
  }

  _collapseItemsPanel(itemsPanel) {
    itemsPanel.style.maxHeight = '0';
  }

  handleMobileDrawerToggle() {
    if (window.innerWidth > 1024) return;
    const summary = this.querySelector('.bundle-builder__panel--summary');
    if (!summary) return;
    const isOpen = summary.classList.toggle('is-drawer-open');
    const toggle = this.querySelector('[data-bundle-mobile-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    const itemsPanel = this._getMobileItemsPanel();
    if (itemsPanel) {
      if (isOpen) {
        this._expandItemsPanel(itemsPanel);
      } else {
        this._collapseItemsPanel(itemsPanel);
      }
    }
  }

  closeMobileDrawer() {
    const summary = this.querySelector('.bundle-builder__panel--summary');
    if (!summary || !summary.classList.contains('is-drawer-open')) return;
    summary.classList.remove('is-drawer-open');
    const toggle = this.querySelector('[data-bundle-mobile-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    const itemsPanel = this._getMobileItemsPanel();
    if (itemsPanel) this._collapseItemsPanel(itemsPanel);
  }

  updateMobileItemCount() {
    const countEl = this.querySelector('[data-bundle-summary-count]');
    const wordEl = this.querySelector('[data-bundle-summary-item-word]');
    if (!countEl) return;
    const total = this.getSelectedProducts().reduce((sum, p) => sum + (p.quantity || 1), 0);
    countEl.textContent = String(total);
    if (wordEl) wordEl.textContent = total === 1 ? 'Item' : 'Items';
  }

  onDocumentClickLookFor(event) {
    const root = this.querySelector('[data-bundle-look-for]');
    if (!root || root.contains(event.target)) {
      return;
    }
    this.closeCollectionLookForMenu();
  }

  onDocumentKeydownLookFor(event) {
    if (event.key !== 'Escape') {
      return;
    }
    this.closeCollectionLookForMenu();
  }

  getCollectionLookForTrigger() {
    return this.querySelector('[data-bundle-look-for-trigger]');
  }

  getCollectionLookForMenu() {
    return this.querySelector('[data-bundle-look-for-menu]');
  }

  closeCollectionLookForMenu() {
    const trigger = this.getCollectionLookForTrigger();
    const menu = this.getCollectionLookForMenu();
    if (!trigger || !menu) {
      return;
    }
    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
  }

  toggleCollectionLookForMenu() {
    const trigger = this.getCollectionLookForTrigger();
    const menu = this.getCollectionLookForMenu();
    if (!trigger || !menu) {
      return;
    }
    const nextOpen = menu.hidden;
    menu.hidden = !nextOpen;
    trigger.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
  }

  setLookForAriaSelected(activeButton) {
    this.querySelectorAll('[data-bundle-look-for-option], [data-bundle-look-for-all]').forEach((btn) => {
      btn.setAttribute('aria-selected', btn === activeButton ? 'true' : 'false');
    });
  }

  showAllBundleCollections() {
    const heading = this.querySelector('[data-bundle-collection-heading]');
    if (heading) {
      heading.hidden = true;
    }
    this.querySelectorAll('[data-bundle-collection-group]').forEach((group) => {
      group.hidden = false;
    });
    this.querySelectorAll('[data-bundle-collection-group-title]').forEach((titleEl) => {
      titleEl.hidden = false;
    });
    const allButton = this.querySelector('[data-bundle-look-for-all]');
    if (allButton) {
      this.setLookForAriaSelected(allButton);
    }
    this.closeCollectionLookForMenu();
  }

  selectBundleCollection(optionButton) {
    const index = optionButton.dataset.collectionIndex;
    if (index === undefined) {
      return;
    }
    const heading = this.querySelector('[data-bundle-collection-heading]');
    if (heading) {
      heading.textContent = optionButton.textContent.trim();
      heading.hidden = false;
    }
    this.querySelectorAll('[data-bundle-collection-group]').forEach((group) => {
      group.hidden = group.dataset.collectionIndex !== index;
    });
    this.querySelectorAll('[data-bundle-collection-group-title]').forEach((titleEl) => {
      titleEl.hidden = true;
    });
    this.setLookForAriaSelected(optionButton);
    this.closeCollectionLookForMenu();
  }

  onComponentClick(event) {
    const addToCartButton = event.target.closest('[data-bundle-add-to-cart]');
    if (addToCartButton && this.contains(addToCartButton)) {
      this.handleAddBundleToCartClick();
      return;
    }

    const removeButton = event.target.closest('[data-bundle-remove]');
    if (removeButton && this.contains(removeButton)) {
      this.handleRemoveClick(removeButton);
      return;
    }

    const cardQtyButton = event.target.closest('[data-bundle-card-qty-action]');
    if (cardQtyButton && this.contains(cardQtyButton)) {
      this.handleCardQtyClick(cardQtyButton);
      return;
    }

    const qtyButton = event.target.closest('[data-bundle-qty-action]');
    if (qtyButton && this.contains(qtyButton)) {
      this.handleQtyClick(qtyButton);
      return;
    }

    const giftOptionButton = event.target.closest('[data-bundle-gift-option]');
    if (giftOptionButton && this.contains(giftOptionButton)) {
      this.handleGiftOptionClick(giftOptionButton);
      return;
    }

    const giftSubmitButton = event.target.closest('[data-bundle-gift-submit]');
    if (giftSubmitButton && this.contains(giftSubmitButton)) {
      this.handleGiftSubmitClick(giftSubmitButton);
      return;
    }

    const giftOpenButton = event.target.closest('[data-bundle-gift-open]');
    if (giftOpenButton && this.contains(giftOpenButton)) {
      this.handleGiftModalOpen();
      return;
    }

    const giftCloseButton = event.target.closest('[data-bundle-gift-close]');
    if (giftCloseButton && this.contains(giftCloseButton)) {
      this.handleGiftModalClose();
      return;
    }

    const mobileToggle = event.target.closest('[data-bundle-mobile-toggle]');
    if (mobileToggle && this.contains(mobileToggle)) {
      this.handleMobileDrawerToggle();
      return;
    }

    const lookForTrigger = event.target.closest('[data-bundle-look-for-trigger]');
    if (lookForTrigger && this.contains(lookForTrigger)) {
      event.preventDefault();
      this.toggleCollectionLookForMenu();
      return;
    }

    const lookForAll = event.target.closest('[data-bundle-look-for-all]');
    if (lookForAll && this.contains(lookForAll)) {
      event.preventDefault();
      this.showAllBundleCollections();
      return;
    }

    const lookForOption = event.target.closest('[data-bundle-look-for-option]');
    if (lookForOption && this.contains(lookForOption)) {
      event.preventDefault();
      this.selectBundleCollection(lookForOption);
      return;
    }

    const addButton = event.target.closest('.bundle-builder__card-button');
    if (!addButton || !this.contains(addButton)) {
      return;
    }

    const card = addButton.closest('.bundle-builder__card');
    if (!card) {
      return;
    }

    const { productId } = card.dataset;
    if (!productId) {
      return;
    }

    if (this.selectedProductIds.has(productId)) {
      return;
    }

    this.selectedProductIds.add(productId);
    this.selectedProductQuantities.set(productId, 1);
    this.syncSelectedProductIds();
  }

  handleRemoveClick(removeButton) {
    const renderedItem = removeButton.closest('[data-bundle-rendered-item]');
    if (!renderedItem) {
      return;
    }

    if (renderedItem.dataset.bundleGiftItem === 'true') {
      const giftPromotionId = renderedItem.dataset.giftPromotionId;
      if (giftPromotionId) {
        const giftOptionKey = renderedItem.dataset.giftOptionKey || '';
        const existingSelection = this.selectedGiftSelections.get(giftPromotionId);
        if (giftOptionKey && Array.isArray(existingSelection)) {
          const nextSelection = existingSelection.filter((optionKey) => optionKey !== giftOptionKey);
          if (nextSelection.length) {
            this.selectedGiftSelections.set(giftPromotionId, nextSelection);
          } else {
            this.selectedGiftSelections.delete(giftPromotionId);
          }
        } else {
          this.selectedGiftSelections.delete(giftPromotionId);
        }
      }
      this.syncSelectedProductIds();
      return;
    }

    const { productId } = renderedItem.dataset;
    if (!productId) {
      return;
    }

    this.selectedProductIds.delete(productId);
    this.selectedProductQuantities.delete(productId);
    this.syncSelectedProductIds();
  }

  handleQtyClick(qtyButton) {
    const renderedItem = qtyButton.closest('[data-bundle-rendered-item]');
    if (!renderedItem) {
      return;
    }

    const { productId } = renderedItem.dataset;
    if (!productId || !this.selectedProductIds.has(productId)) {
      return;
    }

    const action = qtyButton.dataset.bundleQtyAction;
    const currentQty = this.selectedProductQuantities.get(productId) || 1;
    if (action === 'decrease' && currentQty <= 1) {
      this.selectedProductIds.delete(productId);
      this.selectedProductQuantities.delete(productId);
      this.syncSelectedProductIds();
      return;
    }

    const nextQty = action === 'increase' ? currentQty + 1 : currentQty - 1;
    this.selectedProductQuantities.set(productId, Math.max(1, nextQty));
    this.syncSelectedProductIds();
  }

  handleCardQtyClick(qtyButton) {
    const card = qtyButton.closest('.bundle-builder__card');
    if (!card) {
      return;
    }

    const { productId } = card.dataset;
    if (!productId || !this.selectedProductIds.has(productId)) {
      return;
    }

    const action = qtyButton.dataset.bundleCardQtyAction;
    const currentQty = this.selectedProductQuantities.get(productId) || 1;
    if (action === 'decrease' && currentQty <= 1) {
      this.selectedProductIds.delete(productId);
      this.selectedProductQuantities.delete(productId);
      this.syncSelectedProductIds();
      return;
    }

    const nextQty = action === 'increase' ? currentQty + 1 : currentQty - 1;
    this.selectedProductQuantities.set(productId, Math.max(1, nextQty));
    this.syncSelectedProductIds();
  }

  buildProductIndex() {
    const cards = this.querySelectorAll('.bundle-builder__card[data-product-id]');

    cards.forEach((card) => {
      const {
        productId,
        productUrl,
        productTitle,
        productImage,
        productPrice,
        productPriceCents,
        variantId,
        variantTitle,
        variantPrice,
        variantPriceCents
      } = card.dataset;
      if (!productId) {
        return;
      }

      this.productIndex.set(productId, {
        id: productId,
        url: productUrl || '',
        title: productTitle || '',
        image: productImage || '',
        price: productPrice || '',
        priceCents: Number(productPriceCents) || 0,
        firstVariant: {
          id: variantId || '',
          title: variantTitle || '',
          price: variantPrice || '',
          priceCents: Number(variantPriceCents) || 0
        }
      });
    });
  }

  buildPromotions() {
    const promotionsScript = this.querySelector('[data-bundle-promotions]');
    if (!promotionsScript) {
      this.promotions = [];
      return;
    }

    try {
      const parsed = JSON.parse(promotionsScript.textContent || '[]');
      const promotions = Array.isArray(parsed) ? parsed : [];
      this.promotions = promotions
        .filter((promo) => promo && promo.enabled)
        .sort((a, b) => (Number(a.threshold) || 0) - (Number(b.threshold) || 0));
    } catch (error) {
      console.error('BundleBuilder promotions parse error:', error);
      this.promotions = [];
    }
  }

  getProductInfoById(productId) {
    return this.productIndex.get(String(productId)) || null;
  }

  getSelectedProducts() {
    return Array.from(this.selectedProductIds)
      .map((productId) => {
        const product = this.getProductInfoById(productId);
        if (!product) {
          return null;
        }

        return {
          ...product,
          quantity: this.selectedProductQuantities.get(productId) || 1
        };
      })
      .filter(Boolean);
  }

  getProductUnitPriceCents(product) {
    if (!product) {
      return 0;
    }
    if (product.firstVariant && product.firstVariant.priceCents) {
      return product.firstVariant.priceCents;
    }
    return product.priceCents || 0;
  }

  getSelectedSubtotalCents(selectedProducts) {
    return selectedProducts.reduce((subtotal, product) => {
      const quantity = product.quantity || 1;
      const unitPriceCents = this.getProductUnitPriceCents(product);
      return subtotal + unitPriceCents * quantity;
    }, 0);
  }

  getBundleTotalsCents(selectedProducts, activeDiscountPromotion) {
    return selectedProducts.reduce(
      (totals, product) => {
        const quantity = Math.max(1, Number(product.quantity) || 1);
        const originalUnit = this.getProductUnitPriceCents(product);
        const discountedUnit = this.getDiscountedUnitPriceCents(originalUnit, activeDiscountPromotion);
        totals.compare += originalUnit * quantity;
        totals.final += discountedUnit * quantity;
        return totals;
      },
      { compare: 0, final: 0 }
    );
  }

  getDiscountedUnitPriceCents(unitPriceCents, activeDiscountPromotion) {
    if (!activeDiscountPromotion || activeDiscountPromotion.type !== 'discount') {
      return unitPriceCents;
    }

    const discountValue = Number(activeDiscountPromotion.discountValue) || 0;
    if (discountValue <= 0) {
      return unitPriceCents;
    }

    if (activeDiscountPromotion.discountType === 'percentage') {
      const reduced = Math.round(unitPriceCents * (100 - discountValue) / 100);
      return Math.max(0, reduced);
    }

    if (activeDiscountPromotion.discountType === 'fixed') {
      const reduced = unitPriceCents - Math.round(discountValue * 100);
      return Math.max(0, reduced);
    }

    return unitPriceCents;
  }

  getEligiblePromotions(subtotalCents) {
    return this.promotions.filter((promo) => {
      const thresholdCents = (Number(promo.threshold) || 0) * 100;
      return thresholdCents > 0 && subtotalCents >= thresholdCents;
    });
  }

  getActiveDiscountPromotion(eligiblePromotions) {
    const discountPromotions = eligiblePromotions.filter((promo) => promo.type === 'discount');
    if (!discountPromotions.length) {
      return null;
    }
    return discountPromotions[discountPromotions.length - 1];
  }

  getActiveGiftPromotion(eligiblePromotions) {
    const giftPromotions = eligiblePromotions.filter((promo) => promo.type === 'gwp');
    if (!giftPromotions.length) {
      return null;
    }
    return giftPromotions[giftPromotions.length - 1];
  }

  getPromotionKey(promotion) {
    if (!promotion) {
      return '';
    }
    return String(promotion.id || promotion.name || '');
  }

  getGiftOptions(giftPromotion) {
    if (!giftPromotion || !Array.isArray(giftPromotion.gwpItems)) {
      return [];
    }

    return giftPromotion.gwpItems
      .map((giftSource, index) => {
        const giftVariant =
          giftSource && Array.isArray(giftSource.variants) && giftSource.variants.length
            ? giftSource.variants[0]
            : null;
        const giftVariantId = this.getNormalizedVariantId(giftVariant ? giftVariant.id : null);
        if (!giftVariantId) {
          return null;
        }
        const optionKey = `${this.getPromotionKey(giftPromotion)}::${giftVariantId}::${index}`;
        return {
          optionKey,
          variantId: giftVariantId,
          priceCents: Number(giftVariant && giftVariant.price ? giftVariant.price : 0),
          title: (giftSource && giftSource.title) || giftPromotion.name || 'Free gift',
          url: giftSource && giftSource.handle ? `/products/${giftSource.handle}` : '',
          image:
            giftVariant && giftVariant.image && giftVariant.image.originalSrc
              ? giftVariant.image.originalSrc
              : ''
        };
      })
      .filter(Boolean);
  }

  getGiftSelectionLimit(giftPromotion, options = []) {
    if (!giftPromotion) {
      return 0;
    }

    if (giftPromotion.customerReceivesAll) {
      return options.length;
    }

    const maxProductsSelectable = Number(giftPromotion.maxProductsSelectable) || 0;
    const quantitySelectable = Number(giftPromotion.quantitySelectable) || 0;
    return Math.max(1, maxProductsSelectable || quantitySelectable || 1);
  }

  getSelectedGiftOptions(giftPromotion) {
    if (!giftPromotion) {
      return [];
    }
    const promotionKey = this.getPromotionKey(giftPromotion);
    if (!promotionKey) {
      return [];
    }

    const options = this.getGiftOptions(giftPromotion);
    if (giftPromotion.customerReceivesAll) {
      return options;
    }

    const storedSelection = this.selectedGiftSelections.get(promotionKey);
    const selectedOptionKeys = Array.isArray(storedSelection)
      ? storedSelection
      : storedSelection
        ? [storedSelection]
        : [];

    return options.filter((option) => selectedOptionKeys.includes(option.optionKey));
  }

  handleGiftOptionClick(button) {
    const promotionId = button.dataset.giftPromotionId || '';
    const giftOptionKey = button.dataset.giftOptionKey || '';
    if (!promotionId || !giftOptionKey) {
      return;
    }

    const { activeGiftPromotion } = this.getCurrentPricingContext();
    if (!activeGiftPromotion || this.getPromotionKey(activeGiftPromotion) !== promotionId) {
      return;
    }

    const options = this.getGiftOptions(activeGiftPromotion);
    const selectionLimit = this.getGiftSelectionLimit(activeGiftPromotion, options);
    const pendingSelectionSet = new Set(
      this.pendingGiftSelection.promotionId === promotionId ? this.pendingGiftSelection.optionKeys : []
    );

    if (pendingSelectionSet.has(giftOptionKey)) {
      pendingSelectionSet.delete(giftOptionKey);
    } else if (pendingSelectionSet.size < selectionLimit) {
      pendingSelectionSet.add(giftOptionKey);
    }

    this.pendingGiftSelection = {
      promotionId,
      optionKeys: Array.from(pendingSelectionSet)
    };

    this.updateGiftOptionSelection(pendingSelectionSet, selectionLimit);
  }

  updateGiftOptionSelection(pendingSelectionSet, selectionLimit) {
    const optionButtons = this.querySelectorAll('[data-bundle-gift-option]');
    const giftSubmitButton = this.querySelector('[data-bundle-gift-submit]');

    optionButtons.forEach((optionButton) => {
      const optionKey = optionButton.dataset.giftOptionKey || '';
      const isPending = pendingSelectionSet.has(optionKey);
      optionButton.classList.toggle('is-selected', isPending);
      optionButton.setAttribute('aria-pressed', isPending ? 'true' : 'false');
      optionButton.disabled = !isPending && pendingSelectionSet.size >= selectionLimit;
    });

    if (giftSubmitButton) {
      giftSubmitButton.disabled = pendingSelectionSet.size === 0;
    }
  }

  handleGiftSubmitClick(button) {
    const promotionId = button.dataset.giftPromotionId || this.pendingGiftSelection.promotionId;
    if (!promotionId || this.pendingGiftSelection.promotionId !== promotionId) {
      return;
    }

    const optionKeys = [...this.pendingGiftSelection.optionKeys];
    if (optionKeys.length) {
      this.selectedGiftSelections.set(promotionId, optionKeys);
    } else {
      this.selectedGiftSelections.delete(promotionId);
    }

    this.pendingGiftSelection = { promotionId: '', optionKeys: [] };
    this.handleGiftModalClose();
    this.syncSelectedProductIds();
  }

  handleGiftModalOpen() {
    const { activeGiftPromotion } = this.getCurrentPricingContext();
    const promotionKey = this.getPromotionKey(activeGiftPromotion);
    if (!promotionKey) {
      return;
    }

    const selectedOptions = this.getSelectedGiftOptions(activeGiftPromotion);
    this.pendingGiftSelection = {
      promotionId: promotionKey,
      optionKeys: selectedOptions.map((option) => option.optionKey)
    };

    this.renderGiftSelector(activeGiftPromotion, { resetScroll: true });

    const giftModal = this.querySelector('[data-bundle-gift-modal]');
    if (!giftModal) {
      return;
    }
    giftModal.hidden = false;
    const summaryPanel = this.querySelector('.bundle-builder__panel--summary');
    if (summaryPanel) {
      summaryPanel.classList.add('is-gift-modal-open');
    }
  }

  handleGiftModalClose() {
    const summaryPanel = this.querySelector('.bundle-builder__panel--summary');
    if (summaryPanel) {
      summaryPanel.classList.remove('is-gift-modal-open');
    }
    const giftModal = this.querySelector('[data-bundle-gift-modal]');
    if (!giftModal) {
      return;
    }
    this.pendingGiftSelection = { promotionId: '', optionKeys: [] };
    giftModal.hidden = true;
  }

  renderGiftSelector(activeGiftPromotion, { resetScroll = false } = {}) {
    const giftSelector = this.querySelector('[data-bundle-gift-selector]');
    const giftTrigger = this.querySelector('[data-bundle-gift-open]');
    const giftModal = this.querySelector('[data-bundle-gift-modal]');
    const giftOptionsContainer = this.querySelector('[data-bundle-gift-options]');
    const giftSubmitButton = this.querySelector('[data-bundle-gift-submit]');
    const giftHelper = this.querySelector('[data-bundle-gift-helper]');
    if (!giftSelector || !giftOptionsContainer || !giftTrigger || !giftModal || !giftSubmitButton || !giftHelper) {
      return;
    }

    const savedScrollLeft = resetScroll ? 0 : giftOptionsContainer.scrollLeft;

    giftOptionsContainer.innerHTML = '';
    if (!activeGiftPromotion) {
      this.pendingGiftSelection = { promotionId: '', optionKeys: [] };
      giftSelector.hidden = true;
      giftModal.hidden = true;
      giftSubmitButton.disabled = true;
      delete giftSubmitButton.dataset.giftPromotionId;
      giftHelper.hidden = true;
      giftHelper.textContent = '';
      return;
    }

    const promotionKey = this.getPromotionKey(activeGiftPromotion);
    const options = this.getGiftOptions(activeGiftPromotion);
    if (!promotionKey || !options.length) {
      this.pendingGiftSelection = { promotionId: '', optionKeys: [] };
      giftSelector.hidden = true;
      giftModal.hidden = true;
      giftSubmitButton.disabled = true;
      delete giftSubmitButton.dataset.giftPromotionId;
      giftHelper.hidden = true;
      giftHelper.textContent = '';
      return;
    }

    if (activeGiftPromotion.customerReceivesAll) {
      this.pendingGiftSelection = { promotionId: '', optionKeys: [] };
      giftSelector.hidden = true;
      giftModal.hidden = true;
      giftSubmitButton.disabled = true;
      delete giftSubmitButton.dataset.giftPromotionId;
      giftHelper.hidden = true;
      giftHelper.textContent = '';
      return;
    }

    const selectedOptions = this.getSelectedGiftOptions(activeGiftPromotion);
    if (this.pendingGiftSelection.promotionId && this.pendingGiftSelection.promotionId !== promotionKey) {
      this.pendingGiftSelection = { promotionId: '', optionKeys: [] };
    }
    const pendingOptionKeys = new Set(
      this.pendingGiftSelection.promotionId === promotionKey ? this.pendingGiftSelection.optionKeys : []
    );
    const selectionLimit = this.getGiftSelectionLimit(activeGiftPromotion, options);

    if (selectionLimit < options.length) {
      giftHelper.textContent = `Select up to ${selectionLimit} gifts`;
      giftHelper.hidden = false;
    } else {
      giftHelper.textContent = '';
      giftHelper.hidden = true;
    }

    const defaultLabel = giftTrigger.dataset.defaultLabel || `Choose your free gift${selectionLimit > 1 ? 's' : ''}`;
    if (selectedOptions.length === 1) {
      giftTrigger.textContent = `Gift selected: ${selectedOptions[0].title}`;
    } else if (selectedOptions.length > 1) {
      giftTrigger.textContent = `${selectedOptions.length} gifts selected`;
    } else {
      giftTrigger.textContent = defaultLabel;
    }
    giftTrigger.classList.toggle('is-selected', selectedOptions.length > 0);

    options.forEach((option) => {
      const optionButton = document.createElement('button');
      optionButton.type = 'button';
      optionButton.className = 'bundle-builder__gift-option';
      optionButton.setAttribute('data-bundle-gift-option', '');
      optionButton.dataset.giftPromotionId = promotionKey;
      optionButton.dataset.giftOptionKey = option.optionKey;
      const imageWrap = document.createElement('div');
      imageWrap.className = 'bundle-builder__gift-option-image-wrap';

      if (option.image) {
        const optionImage = document.createElement('img');
        optionImage.className = 'bundle-builder__gift-option-image';
        optionImage.src = option.image;
        optionImage.alt = option.title || 'Free gift';
        optionImage.loading = 'lazy';
        imageWrap.appendChild(optionImage);
      }

      const optionBadge = document.createElement('span');
      optionBadge.className = 'bundle-builder__gift-option-badge';
      optionBadge.textContent = 'FREE';
      imageWrap.appendChild(optionBadge);
      optionButton.appendChild(imageWrap);

      const optionTitle = document.createElement('p');
      optionTitle.className = 'bundle-builder__gift-option-title';
      optionTitle.textContent = option.title || 'Free gift';
      optionButton.appendChild(optionTitle);

      const checkMark = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      checkMark.setAttribute('class', 'bundle-builder__gift-option-check');
      checkMark.setAttribute('width', '23');
      checkMark.setAttribute('height', '23');
      checkMark.setAttribute('viewBox', '0 0 23 23');
      checkMark.setAttribute('fill', 'none');
      checkMark.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      checkMark.innerHTML = '<circle cx="11.2246" cy="11.2246" r="11.2246" fill="black"/><path d="M4.45312 11.3014L9.17302 15.9657L17.7362 7.40238" stroke="white" stroke-width="2" stroke-linecap="round"/>';
      optionButton.appendChild(checkMark);

      const isPending = pendingOptionKeys.has(option.optionKey);
      optionButton.classList.toggle(
        'is-selected',
        isPending
      );
      optionButton.setAttribute('aria-pressed', isPending ? 'true' : 'false');
      optionButton.disabled = !isPending && pendingOptionKeys.size >= selectionLimit;
      giftOptionsContainer.appendChild(optionButton);
    });

    giftSubmitButton.dataset.giftPromotionId = promotionKey;
    giftSubmitButton.disabled = pendingOptionKeys.size === 0;
    giftSelector.hidden = false;
    this.initGiftOptionsSlider({ scrollLeft: savedScrollLeft });
  }

  teardownGiftOptionsSlider() {
    if (this.giftSliderResizeObserver) {
      this.giftSliderResizeObserver.disconnect();
      this.giftSliderResizeObserver = null;
    }
    if (this.giftSliderScrollHandler && this.giftOptionsTrack) {
      this.giftOptionsTrack.removeEventListener('scroll', this.giftSliderScrollHandler);
    }
    this.giftSliderScrollHandler = null;
    this.giftOptionsTrack = null;
  }

  initGiftOptionsSlider({ scrollLeft = 0 } = {}) {
    this.teardownGiftOptionsSlider();

    const slider = this.querySelector('[data-bundle-gift-slider]');
    const track = this.querySelector('[data-bundle-gift-options]');
    const prevButton = this.querySelector('[data-bundle-gift-prev]');
    const nextButton = this.querySelector('[data-bundle-gift-next]');
    if (!slider || !track || !prevButton || !nextButton) {
      return;
    }

    this.giftOptionsTrack = track;

    const updateNav = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const scrollable = maxScroll > 1;
      slider.classList.toggle('is-scrollable', scrollable);
      prevButton.disabled = !scrollable || track.scrollLeft <= 1;
      nextButton.disabled = !scrollable || track.scrollLeft >= maxScroll - 1;
    };

    this.giftSliderScrollHandler = updateNav;
    track.addEventListener('scroll', updateNav, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.giftSliderResizeObserver = new ResizeObserver(() => updateNav());
      this.giftSliderResizeObserver.observe(track);
    }

    const scrollByDirection = (direction) => {
      const firstSlide = track.querySelector('.bundle-builder__gift-option');
      if (!firstSlide) {
        return;
      }
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
      const distance = firstSlide.getBoundingClientRect().width + gap;
      track.scrollBy({ left: direction * distance, behavior: 'smooth' });
    };

    prevButton.onclick = () => scrollByDirection(-1);
    nextButton.onclick = () => scrollByDirection(1);

    track.scrollLeft = scrollLeft;
    requestAnimationFrame(() => {
      updateNav();
      requestAnimationFrame(updateNav);
    });
  }

  getNormalizedVariantId(rawId) {
    if (!rawId && rawId !== 0) {
      return null;
    }

    const idString = String(rawId);
    if (/^\d+$/.test(idString)) {
      return Number(idString);
    }

    const gidMatch = idString.match(/(\d+)$/);
    return gidMatch ? Number(gidMatch[1]) : null;
  }

  getCurrentPricingContext() {
    const selectedProducts = this.getSelectedProducts();
    const subtotalCents = this.getSelectedSubtotalCents(selectedProducts);
    const eligiblePromotions = this.getEligiblePromotions(subtotalCents);
    const activeGiftPromotion = this.getActiveGiftPromotion(eligiblePromotions);
    const selectedGiftOptions = this.getSelectedGiftOptions(activeGiftPromotion);
    return {
      selectedProducts,
      subtotalCents,
      eligiblePromotions,
      activeGiftPromotion,
      selectedGiftOptions
    };
  }

  buildCartItems(ritualBundleId = '') {
    const { selectedProducts, activeGiftPromotion, selectedGiftOptions, eligiblePromotions } = this.getCurrentPricingContext();
    const activeDiscountPromotion = this.getActiveDiscountPromotion(eligiblePromotions);

    const ritualItems = selectedProducts
      .map((product) => {
        const variantId = this.getNormalizedVariantId(
          product.firstVariant && product.firstVariant.id ? product.firstVariant.id : null
        );
        if (!variantId) {
          return null;
        }
        const quantity = Math.max(1, Number(product.quantity) || 1);
        const priceCents = this.getProductUnitPriceCents(product);
        return {
          variantId: `gid://shopify/ProductVariant/${variantId}`,
          price: (priceCents / 100).toFixed(2),
          quantity
        };
      })
      .filter(Boolean);

    const gwpItems = [];
    if (activeGiftPromotion && selectedGiftOptions.length) {
      const tierId = activeGiftPromotion.id || activeGiftPromotion.name || 'gwp';
      selectedGiftOptions.forEach((giftOption) => {
        const giftVariantId = this.getNormalizedVariantId(giftOption.variantId);
        if (!giftVariantId) {
          return;
        }
        gwpItems.push({
          variantId: `gid://shopify/ProductVariant/${giftVariantId}`,
          price: (giftOption.priceCents / 100).toFixed(2),
          quantity: 1,
          gwpTierId: tierId
        });
      });
    }

    const allItems = [...ritualItems, ...gwpItems];

    const masterVariantId = this.getNormalizedVariantId(this.dataset.masterVariantId);
    if (!masterVariantId) {
      console.warn('BundleBuilder: ritual-bundle master variant ID not found. Check that the product with handle "ritual-bundle" exists in the store.');
    }

    if (!masterVariantId || !allItems.length) {
      return [];
    }

    const totals = this.getBundleTotalsCents(selectedProducts, activeDiscountPromotion);

    const properties = { _hp_ritual_items: JSON.stringify(allItems) };
    if (ritualBundleId) {
      properties._hp_ritual = ritualBundleId;
      properties._hp_ritual_role = 'master';
    }
    if (activeDiscountPromotion) {
      properties._hp_ritual_discount_type = activeDiscountPromotion.discountType || '';
      properties._hp_ritual_discount_value = String(activeDiscountPromotion.discountValue || '');
      properties._hp_ritual_discount_name = activeDiscountPromotion.name || '';
    }
    if (totals.compare > totals.final) {
      properties._hp_ritual_compare_price = String(totals.compare);
    }

    return [{
      id: masterVariantId,
      quantity: 1,
      properties
    }];
  }

  setCartMessage(message, isError) {
    const messageElement = this.querySelector('[data-bundle-cart-message]');
    if (!messageElement) {
      return;
    }

    if (!message) {
      messageElement.hidden = true;
      messageElement.textContent = '';
      messageElement.removeAttribute('data-error');
      return;
    }

    messageElement.hidden = false;
    messageElement.textContent = message;
    if (isError) {
      messageElement.setAttribute('data-error', 'true');
    } else {
      messageElement.removeAttribute('data-error');
    }
  }

  /**
   * Same as `snippets/svg-cart-icon.liquid`: total items minus Route protection line items.
   */
  getCartDisplayItemCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') {
      return 0;
    }
    let count = cart.item_count;
    const items = cart.items;
    if (!items || !items.length) {
      return Math.max(0, count);
    }
    items.forEach((item) => {
      if (item.vendor === 'Route') {
        count -= item.quantity;
      }
    });
    return Math.max(0, count);
  }

  /**
   * Header icon is not part of `cart?view=header` — sync count + flower states from /cart.js.
   */
  applyHeaderCartIconCount(displayCount) {
    const scope = document.querySelector('.navbar__atc');
    if (!scope) {
      return;
    }

    const n = Math.max(0, Math.floor(Number(displayCount) || 0));

    const countEl = scope.querySelector('#cart__count') || scope.querySelector('text.cart__count');
    if (countEl) {
      countEl.textContent = n > 0 ? String(n) : '';
    }

    const top = scope.querySelector('.cart-icon__top');
    if (top) {
      top.classList.toggle('d-none', n === 0);
      top.classList.toggle('d-flex', n > 0);
    }

    scope.querySelectorAll('g.cart-flower.cart-flower-10').forEach((el) => {
      el.classList.toggle('active', n >= 10);
    });
    scope.querySelectorAll('g.cart-flower.cart-flower-2:not(.cart-flower-10)').forEach((el) => {
      el.classList.toggle('active', n >= 2);
    });
    scope.querySelectorAll('g.cart-flower.cart-flower-1:not(.cart-flower-2)').forEach((el) => {
      el.classList.toggle('active', n >= 1);
    });
  }

  async syncHeaderCartIconFromServer() {
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    try {
      const response = await fetch(`${root}cart.js`);
      if (!response.ok) {
        return;
      }
      const cart = await response.json();
      this.applyHeaderCartIconCount(this.getCartDisplayItemCount(cart));
      const atc = document.querySelector('.navbar__atc');
      if (atc) {
        atc.classList.toggle('cart-icon-filled', (cart.item_count || 0) > 0);
      }
    } catch (e) {
      console.error('Bundle builder: cart icon sync failed', e);
    }
  }

  getExpectedBundleTotals() {
    const { selectedProducts, eligiblePromotions } = this.getCurrentPricingContext();
    const activeDiscountPromotion = this.getActiveDiscountPromotion(eligiblePromotions);
    return this.getBundleTotalsCents(selectedProducts, activeDiscountPromotion);
  }

  getCartApiRoot() {
    return window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  }

  async fetchCartJson() {
    try {
      const response = await fetch(`${this.getCartApiRoot()}cart.js`);
      if (!response.ok) {
        return null;
      }
      return response.json();
    } catch (e) {
      console.error('Bundle builder: cart.js fetch failed', e);
      return null;
    }
  }

  findRitualMasterLineItem(cart, ritualBundleId) {
    if (!cart || !Array.isArray(cart.items)) {
      return null;
    }
    return cart.items.find(
      (item) => item.properties && item.properties._hp_ritual === ritualBundleId
    ) || null;
  }

  getLineItemFinalCents(lineItem) {
    if (!lineItem) {
      return 0;
    }
    if (typeof lineItem.final_line_price === 'number') {
      return lineItem.final_line_price;
    }
    return typeof lineItem.line_price === 'number' ? lineItem.line_price : 0;
  }

  ritualCartPricingReady(cart, ritualBundleId, expectedFinalCents, expectedCompareCents) {
    const master = this.findRitualMasterLineItem(cart, ritualBundleId);
    if (!master) {
      return false;
    }
    if (expectedCompareCents <= expectedFinalCents) {
      return true;
    }
    const lineFinal = this.getLineItemFinalCents(master);
    if (Math.abs(lineFinal - expectedFinalCents) <= 1) {
      return true;
    }
    return lineFinal > 0 && lineFinal < expectedCompareCents;
  }

  /**
   * Cart transform pricing can lag behind cart/add.js — poll until discounted totals land.
   */
  async waitForRitualCartPricing(ritualBundleId, expectedFinalCents, expectedCompareCents) {
    const maxAttempts = 12;
    const delayMs = 250;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const cart = await this.fetchCartJson();
      if (cart && this.ritualCartPricingReady(cart, ritualBundleId, expectedFinalCents, expectedCompareCents)) {
        return cart;
      }
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    return this.fetchCartJson();
  }

  syncCheckoutSubtotal(cart) {
    if (!cart || typeof cart.items_subtotal_price !== 'number') {
      return;
    }

    let subtotalCents = cart.items_subtotal_price;
    const formatted = this.formatCents(subtotalCents);
    document.querySelectorAll('.cart-subtotal').forEach((element) => {
      element.textContent = formatted;
    });

    const quadpay = document.querySelector('quadpay-widget-v3[amountSelector=".cart-subtotal"]');
    if (quadpay) {
      quadpay.setAttribute('amount', (subtotalCents / 100).toFixed(2));
    }
  }

  syncRitualMasterLinePrices(ritualBundleId, expectedFinalCents, expectedCompareCents) {
    const wrapper = document.querySelector(`[data-ritual-id="${ritualBundleId}"]`);
    if (!wrapper) {
      return;
    }

    const finalElement = wrapper.querySelector('.product-price');
    if (finalElement && expectedFinalCents > 0) {
      finalElement.textContent = this.formatCents(expectedFinalCents);
    }

    const compareElement = wrapper.querySelector('.product-price-compare');
    if (!compareElement) {
      return;
    }

    if (expectedCompareCents > expectedFinalCents) {
      compareElement.textContent = this.formatCents(expectedCompareCents);
      compareElement.hidden = false;
      compareElement.classList.add('strikethrough');
    } else {
      compareElement.hidden = true;
    }
  }

  applyRitualPricingToCartUi(cart, ritualBundleId, expectedFinalCents, expectedCompareCents) {
    if (!cart) {
      return;
    }

    const master = this.findRitualMasterLineItem(cart, ritualBundleId);
    let subtotalCents = cart.items_subtotal_price;

    if (master && expectedCompareCents > expectedFinalCents) {
      const lineFinal = this.getLineItemFinalCents(master);
      if (lineFinal > expectedFinalCents + 1) {
        subtotalCents = Math.max(0, subtotalCents - lineFinal + expectedFinalCents);
      }
    }

    this.syncCheckoutSubtotal({ items_subtotal_price: subtotalCents });
    this.syncRitualMasterLinePrices(ritualBundleId, expectedFinalCents, expectedCompareCents);
  }

  /**
   * Sticky cart (honey-pot.js): reload drawer markup from cart.header view.
   */
  async refreshThemeCartUi() {
    if (typeof window.hpUpdateCart === 'function') {
      try {
        await window.hpUpdateCart(false);
      } catch (e) {
        console.error('Bundle builder: hpUpdateCart failed', e);
      }
    } else {
      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      try {
        const response = await fetch(`${root}cart?view=header`);
        if (response.ok) {
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('.bag-wrapper');
          const bag = document.querySelector('.bag-wrapper');
          if (fresh && bag) {
            bag.innerHTML = fresh.innerHTML;
          }
          const atc = document.querySelector('.navbar__atc');
          if (atc) {
            atc.classList.add('cart-icon-filled');
          }
        }
      } catch (e) {
        console.error('Bundle builder: cart refresh failed', e);
      }
    }

    await this.syncHeaderCartIconFromServer();

    if (typeof window.hpInjectRitualBundleComponents === 'function') {
      window.hpInjectRitualBundleComponents();
    }
  }

  /**
   * Matches theme class `dh` open() — sidebar cart, not Dawn cart-drawer.
   */
  openStickyCartDrawer() {
    if (document.documentElement.classList.contains('template-cart')) {
      return;
    }
    const sidebar = document.querySelector('.sidebar-wrapper');
    const login = document.querySelector('.login-sidebar');
    const wrapper = document.querySelector('.bag-wrapper');
    if (!sidebar || !wrapper) {
      return;
    }
    sidebar.classList.add('open', 'just-opened');
    if (login) {
      login.classList.add('d-none');
    }
    wrapper.classList.remove('d-none');
    document.documentElement.classList.add('scroll-hide');
  }

  async handleAddBundleToCartClick() {
    if (this.isAddingToCart) {
      return;
    }

    const ritualBundleId = `ritual_bundle_${Date.now()}`;
    const { selectedProducts, selectedGiftOptions } = this.getCurrentPricingContext();
    const expectedTotals = this.getExpectedBundleTotals();
    const items = this.buildCartItems(ritualBundleId);
    if (!items.length) {
      this.setCartMessage('Select at least one product to add.', true);
      this.syncAddToCartButtonState();
      return;
    }

    this.isAddingToCart = true;
    this.syncAddToCartButtonState();
    this.setCartMessage('', false);

    try {
      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const sectionsToRender =
        this.cart && typeof this.cart.getSectionsToRender === 'function'
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : [];
      const requestBody = { items };

      if (sectionsToRender.length) {
        requestBody.sections = sectionsToRender;
        requestBody.sections_url = window.location.pathname;
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        const errorMessage =
          (errorPayload && (errorPayload.description || errorPayload.message)) ||
          'Unable to add bundle to cart.';
        throw new Error(errorMessage);
      }

      const responsePayload = await response.json();

      if (this.cart && this.cart.classList && this.cart.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }

      this.saveBundleComponents(ritualBundleId, selectedProducts, selectedGiftOptions);

      let cartUiHandled = false;
      if (this.cart && typeof this.cart.renderContents === 'function' && responsePayload.sections) {
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
        this.cart.renderContents(responsePayload);
        cartUiHandled = true;
      } else if (this.cart && typeof this.cart.open === 'function') {
        this.cart.open();
        cartUiHandled = true;
      }

      if (!cartUiHandled) {
        await this.waitForRitualCartPricing(
          ritualBundleId,
          expectedTotals.final,
          expectedTotals.compare
        );
        await this.refreshThemeCartUi();
        const cart = await this.fetchCartJson();
        this.applyRitualPricingToCartUi(
          cart,
          ritualBundleId,
          expectedTotals.final,
          expectedTotals.compare
        );
        this.openStickyCartDrawer();
      }
      this.closeMobileDrawer();
      this.setCartMessage('Bundle added to cart.', false);
      this.dispatchEvent(
        new CustomEvent('bundle-builder:added-to-cart', {
          detail: { items },
          bubbles: true
        })
      );
    } catch (error) {
      this.setCartMessage(error && error.message ? error.message : 'Unable to add bundle to cart.', true);
    } finally {
      this.isAddingToCart = false;
      this.syncAddToCartButtonState();
    }
  }

  formatCents(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  syncAddButtonState() {
    const addButtons = this.querySelectorAll('.bundle-builder__card-button');
    addButtons.forEach((button) => {
      const card = button.closest('.bundle-builder__card');
      const productId = card ? card.dataset.productId : null;
      const isSelected = productId ? this.selectedProductIds.has(productId) : false;
      const defaultLabel = button.dataset.defaultLabel || 'Add';

      button.textContent = defaultLabel + ' | ' + this.formatCents(card.dataset.productPriceCents);
      button.classList.remove('is-selected');
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      button.hidden = isSelected;
      button.disabled = false;

      const qtyControl = card ? card.querySelector('[data-bundle-card-qty]') : null;
      const qtyValue = qtyControl ? qtyControl.querySelector('[data-bundle-card-qty-value]') : null;
      if (qtyControl) {
        qtyControl.hidden = !isSelected;
      }
      if (qtyValue && productId) {
        qtyValue.textContent = String(this.selectedProductQuantities.get(productId) || 1);
      }
    });
  }

  syncAddToCartButtonState() {
    const addToCartButton = this.querySelector('[data-bundle-add-to-cart]');
    if (!addToCartButton) {
      return;
    }

    const defaultLabel = addToCartButton.dataset.defaultLabel || 'Add bundle to cart';
    const loadingLabel = addToCartButton.dataset.loadingLabel || 'Adding...';
    const hasItems = this.buildCartItems().length > 0;

    addToCartButton.disabled = this.isAddingToCart || !hasItems;
    addToCartButton.textContent = this.isAddingToCart ? loadingLabel : defaultLabel;
  }

  updateBundleTotals(selectedProducts, activeDiscountPromotion) {
    const totalContainer = this.querySelector('[data-bundle-total]');
    const compareElement = this.querySelector('[data-bundle-total-compare]');
    const finalElement = this.querySelector('[data-bundle-total-final]');
    if (!totalContainer || !compareElement || !finalElement) {
      return;
    }

    const hasItems = selectedProducts.length > 0;
    totalContainer.hidden = !hasItems;
    if (!hasItems) {
      return;
    }

    const totals = this.getBundleTotalsCents(selectedProducts, activeDiscountPromotion);
    finalElement.textContent = this.formatCents(totals.final);
    compareElement.textContent = this.formatCents(totals.compare);
    compareElement.hidden = totals.final >= totals.compare;
  }

  renderSelectedProducts() {
    const selectedList = this.querySelector('[data-bundle-selected-list]');
    const itemTemplate = selectedList
      ? selectedList.querySelector('[data-bundle-item-template]')
      : null;
    const emptyState = this.querySelector('[data-bundle-empty-state]');

    if (!selectedList || !itemTemplate) {
      return;
    }

    selectedList.querySelectorAll('[data-bundle-rendered-item]').forEach((item) => item.remove());

    const selectedProducts = this.getSelectedProducts();
    const hasSelectedProducts = selectedProducts.length > 0;
    const subtotalCents = this.getSelectedSubtotalCents(selectedProducts);
    const eligiblePromotions = this.getEligiblePromotions(subtotalCents);
    const activeDiscountPromotion = this.getActiveDiscountPromotion(eligiblePromotions);
    const activeGiftPromotion = this.getActiveGiftPromotion(eligiblePromotions);
    const selectedGiftOptions = this.getSelectedGiftOptions(activeGiftPromotion);

    selectedProducts.forEach((product) => {
      let item = null;
      if (itemTemplate.tagName === 'TEMPLATE') {
        item = itemTemplate.content.firstElementChild
          ? itemTemplate.content.firstElementChild.cloneNode(true)
          : null;
      } else {
        item = itemTemplate.cloneNode(true);
        item.hidden = false;
        item.removeAttribute('data-bundle-item-template');
      }

      if (!item) {
        return;
      }

      item.setAttribute('data-bundle-rendered-item', '');
      item.dataset.productId = product.id;

      const imageContainer = item.querySelector('[data-bundle-item-image]');
      if (imageContainer) {
        imageContainer.innerHTML = '';
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = product.title || '';
          image.loading = 'lazy';
          if (product.url) {
            const imageLink = document.createElement('a');
            imageLink.href = product.url;
            imageLink.appendChild(image);
            imageContainer.appendChild(imageLink);
          } else {
            imageContainer.appendChild(image);
          }
        }
      }

      const nameElement = item.querySelector('[data-bundle-item-name]');
      if (nameElement) {
        nameElement.textContent = '';
        if (product.url) {
          const nameLink = document.createElement('a');
          nameLink.href = product.url;
          nameLink.textContent = product.title || 'Item name';
          nameElement.appendChild(nameLink);
        } else {
          nameElement.textContent = product.title || 'Item name';
        }
      }

      const comparePriceElement = item.querySelector('[data-bundle-item-price-compare]');
      const finalPriceElement = item.querySelector('[data-bundle-item-price-final]');
      const unitOriginalCents = this.getProductUnitPriceCents(product);
      const unitDiscountedCents = this.getDiscountedUnitPriceCents(
        unitOriginalCents,
        activeDiscountPromotion
      );

      if (finalPriceElement) {
        finalPriceElement.textContent = this.formatCents(unitDiscountedCents);
      }

      if (comparePriceElement) {
        comparePriceElement.textContent = this.formatCents(unitOriginalCents);
        comparePriceElement.hidden = unitDiscountedCents >= unitOriginalCents;
      }

      const qtyValueElement = item.querySelector('[data-bundle-item-qty-value]');
      if (qtyValueElement) {
        qtyValueElement.textContent = String(product.quantity || 1);
      }

      selectedList.appendChild(item);
    });

    if (activeGiftPromotion && selectedGiftOptions.length) {
      selectedGiftOptions.forEach((selectedGiftOption) => {
        let giftItem = null;
        if (itemTemplate.tagName === 'TEMPLATE') {
          giftItem = itemTemplate.content.firstElementChild
            ? itemTemplate.content.firstElementChild.cloneNode(true)
            : null;
        } else {
          giftItem = itemTemplate.cloneNode(true);
          giftItem.hidden = false;
          giftItem.removeAttribute('data-bundle-item-template');
        }

        if (!giftItem) {
          return;
        }

        giftItem.setAttribute('data-bundle-rendered-item', '');
        giftItem.setAttribute('data-bundle-gift-item', 'true');
        giftItem.dataset.giftPromotionId = this.getPromotionKey(activeGiftPromotion);
        giftItem.dataset.giftOptionKey = selectedGiftOption.optionKey;

        const giftImageContainer = giftItem.querySelector('[data-bundle-item-image]');
        if (giftImageContainer) {
          giftImageContainer.innerHTML = '';
          if (selectedGiftOption.image) {
            const image = document.createElement('img');
            image.src = selectedGiftOption.image;
            image.alt = selectedGiftOption.title || 'Free gift';
            image.loading = 'lazy';
            if (selectedGiftOption.url) {
              const giftImageLink = document.createElement('a');
              giftImageLink.href = selectedGiftOption.url;
              giftImageLink.appendChild(image);
              giftImageContainer.appendChild(giftImageLink);
            } else {
              giftImageContainer.appendChild(image);
            }
          }
        }

        const giftNameElement = giftItem.querySelector('[data-bundle-item-name]');
        if (giftNameElement) {
          const giftTitle = selectedGiftOption.title || activeGiftPromotion.name || 'Free gift';
          giftNameElement.textContent = '';
          if (selectedGiftOption.url) {
            const giftNameLink = document.createElement('a');
            giftNameLink.href = selectedGiftOption.url;
            giftNameLink.textContent = giftTitle;
            giftNameElement.appendChild(giftNameLink);
          } else {
            giftNameElement.textContent = giftTitle;
          }
        }

        const giftComparePriceElement = giftItem.querySelector('[data-bundle-item-price-compare]');
        const giftFinalPriceElement = giftItem.querySelector('[data-bundle-item-price-final]');
        if (giftComparePriceElement) {
          giftComparePriceElement.hidden = true;
        }
        if (giftFinalPriceElement) {
          giftFinalPriceElement.textContent = 'FREE';
        }

        const giftQty = giftItem.querySelector('[data-bundle-item-qty]');
        if (giftQty) {
          giftQty.hidden = true;
        }

        const giftRemove = giftItem.querySelector('[data-bundle-remove]');
        if (giftRemove) {
          giftRemove.hidden = Boolean(activeGiftPromotion.customerReceivesAll);
        }

        selectedList.appendChild(giftItem);
      });
    }

    this.renderGiftSelector(activeGiftPromotion);

    if (emptyState) {
      emptyState.hidden = hasSelectedProducts || Boolean(activeGiftPromotion);
    }

    this.updateBundleTotals(selectedProducts, activeDiscountPromotion);
    this.updatePromotionUI(subtotalCents, eligiblePromotions);
  }

  updatePromotionUI(subtotalCents, eligiblePromotions) {
    const promoTierElements = Array.from(this.querySelectorAll('[data-bundle-promo-tier]'));
    const progressFill = this.querySelector('[data-bundle-progress-fill]');

    // Mark each tier active/inactive and find the last active one.
    let lastActiveIdx = -1;
    promoTierElements.forEach((tierEl, i) => {
      const thresholdCents = Number(tierEl.dataset.thresholdCents) || 0;
      const isActive = thresholdCents > 0 && subtotalCents >= thresholdCents;
      tierEl.classList.toggle('is-active', isActive);
      if (isActive) lastActiveIdx = i;
    });

    // Fill bar uses the same evenly-distributed index formula as the Liquid:
    // position(i) = (i + 1) / (total + 1) × 100
    if (progressFill && promoTierElements.length > 0) {
      const total = promoTierElements.length;
      const tierPos = (i) => (i + 1) / total * 100;
      let fillPercent = 0;

      if (lastActiveIdx >= 0) {
        const activePos = tierPos(lastActiveIdx);
        if (lastActiveIdx < total - 1) {
          // Interpolate between the last active tier and the next one.
          const curThreshold = Number(promoTierElements[lastActiveIdx].dataset.thresholdCents) || 0;
          const nextThreshold = Number(promoTierElements[lastActiveIdx + 1].dataset.thresholdCents) || 0;
          const nextPos = tierPos(lastActiveIdx + 1);
          const segPct = nextThreshold > curThreshold
            ? Math.min(1, (subtotalCents - curThreshold) / (nextThreshold - curThreshold))
            : 1;
          fillPercent = activePos + (nextPos - activePos) * segPct;
        } else {
          fillPercent = 100;
        }
      } else {
        // No tiers unlocked yet — show partial fill toward the first tier marker.
        const firstThreshold = Number(promoTierElements[0].dataset.thresholdCents) || 0;
        const firstPos = tierPos(0);
        fillPercent = firstThreshold > 0
          ? Math.min(firstPos, (subtotalCents / firstThreshold) * firstPos)
          : 0;
      }

      progressFill.style.width = `${Math.min(100, Math.max(0, fillPercent))}%`;
    }

    const activeDiscountLabel = this.querySelector('[data-bundle-active-discount]');
    if (!activeDiscountLabel) {
      return;
    }

    const activeDiscount = this.getActiveDiscountPromotion(eligiblePromotions);
    activeDiscountLabel.textContent = activeDiscount
      ? `${activeDiscount.name}`
      : activeDiscountLabel.dataset.defaultLabel || activeDiscountLabel.textContent;
    activeDiscountLabel.style.display = activeDiscount ? '' : 'none';
  }

  syncSelectedProductIds() {
    const selectedIdsArray = Array.from(this.selectedProductIds);
    const selectedProducts = this.getSelectedProducts();
    const subtotalCents = this.getSelectedSubtotalCents(selectedProducts);
    const eligiblePromotions = this.getEligiblePromotions(subtotalCents);

    const activeDiscount = this.getActiveDiscountPromotion(eligiblePromotions);
    const activeGift = this.getActiveGiftPromotion(eligiblePromotions);
    const activeGiftKey = this.getPromotionKey(activeGift);
    const activeGiftSelection = activeGiftKey ? this.selectedGiftSelections.get(activeGiftKey) : [];
    this.selectedGiftSelections.clear();
    if (activeGiftKey && Array.isArray(activeGiftSelection) && activeGiftSelection.length) {
      this.selectedGiftSelections.set(activeGiftKey, activeGiftSelection);
    } else if (activeGiftKey && typeof activeGiftSelection === 'string' && activeGiftSelection) {
      this.selectedGiftSelections.set(activeGiftKey, [activeGiftSelection]);
    }
    const selectedGiftOptions = this.getSelectedGiftOptions(activeGift);

    this.dataset.selectedProductIds = JSON.stringify(selectedIdsArray);
    this.dataset.selectedProducts = JSON.stringify(selectedProducts);
    this.dataset.bundleSubtotalCents = String(subtotalCents);
    this.dataset.activeDiscountPromotion = activeDiscount ? JSON.stringify(activeDiscount) : '';
    this.dataset.activeGiftPromotion = activeGift ? JSON.stringify(activeGift) : '';
    this.syncAddButtonState();
    this.renderSelectedProducts();
    this.syncAddToCartButtonState();
    this.updateMobileItemCount();
    // If the mobile drawer is open, re-sync the items panel height after render.
    requestAnimationFrame(() => {
      const summary = this.querySelector('.bundle-builder__panel--summary');
      const itemsPanel = this._getMobileItemsPanel();
      if (summary && summary.classList.contains('is-drawer-open') && itemsPanel) {
        this._expandItemsPanel(itemsPanel);
      }
    });

    this.dispatchEvent(
      new CustomEvent('bundle-builder:selection-changed', {
        detail: {
          selectedProductIds: selectedIdsArray,
          selectedProducts,
          subtotalCents,
          activeDiscountPromotion: activeDiscount,
          activeGiftPromotion: activeGift,
          selectedGiftOptions,
          selectedGiftOption: selectedGiftOptions[0] || null
        },
        bubbles: true
      })
    );
  }

  saveBundleComponents(ritualBundleId, selectedProducts, gwpOptions = []) {
    try {
      const store = JSON.parse(localStorage.getItem('hp_ritual_components') || '{}');
      store[ritualBundleId] = [
        ...selectedProducts.map((p) => ({
          title: p.title,
          image: p.image,
          url: p.url,
          variantTitle: p.firstVariant ? p.firstVariant.title : '',
          quantity: p.quantity || 1
        })),
        ...gwpOptions.map((opt) => ({
          title: opt.title,
          image: opt.image,
          url: opt.url,
          variantTitle: '',
          quantity: 1,
          isGwp: true
        }))
      ];
      localStorage.setItem('hp_ritual_components', JSON.stringify(store));
    } catch (e) {
      // localStorage unavailable
    }
  }

}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}